import type { AxiosResponse } from 'axios';
import Api from '@/data/Api.ts';
import type { CompanyJson } from '@/data/companies/CompanyJson.ts';
import type { TicketJson } from '@/data/tickets/TicketJson.ts';
import StringUtils from '@/infrastructure/support/utils/StringUtils.ts';

const URL_TICKETS = '/companies/{0}/plants/{1}/tickets';
const URL_TICKET_VIA_COMPANY_AND_PLANT =
  '/companies/{0}/plants/{1}/tickets/{2}';

const URL_TICKET_VIA_COMPANY = '/companies/{0}/tickets/{1}';
const URL_TICKET_OWNING_COMPANY = '/tickets/{0}/owning-company';

export default class TicketApi extends Api {
  getTicketById(
    companyId: number,
    ticketId: number,
    params: {} = {},
    includes: any[] = [],
  ): Promise<AxiosResponse<TicketJson>> {
    const url: string = StringUtils.stringFormat(URL_TICKET_VIA_COMPANY, [
      companyId,
      ticketId,
    ]);
    return this.standardGet<TicketJson>(url, params, includes);
  }

  showTicket(
    companyId: number,
    plantId: number,
    ticketId: number,
  ): Promise<AxiosResponse<TicketJson>> {
    return this.standardGet<TicketJson>(
      this.getTicketUrl(companyId, plantId, ticketId),
      {},
      [],
    );
  }

  createTicket(
    companyId: number,
    plantId: number,
    payload: {},
  ): Promise<AxiosResponse<TicketJson>> {
    const url: string = StringUtils.stringFormat(URL_TICKETS, [
      companyId,
      plantId,
    ]);
    return this.standardPost<TicketJson>(url, payload);
  }

  updateTicket(
    companyId: number,
    plantId: number,
    ticketId: number,
    payload: {},
  ): Promise<AxiosResponse<TicketJson>> {
    const url: string = this.getTicketUrl(companyId, plantId, ticketId);
    return this.standardPut<TicketJson>(url, payload);
  }

  attachChildCompany(
    companyId: number,
    plantId: number,
    ticketId: number,
    childCompanyId: number,
  ): Promise<AxiosResponse<void>> {
    const url: string =
      this.getTicketUrl(companyId, plantId, ticketId) +
      '/companies/' +
      childCompanyId;

    return this.standardPut<void>(url, {});
  }

  detachChildCompany(
    companyId: number,
    plantId: number,
    ticketId: number,
    childCompanyId: number,
  ): Promise<AxiosResponse<void>> {
    const url: string =
      this.getTicketUrl(companyId, plantId, ticketId) +
      '/companies/' +
      childCompanyId;

    return this.standardDelete<void>(url);
  }

  closeTicket(
    companyId: number,
    plantId: number,
    ticketId: number,
  ): Promise<AxiosResponse<void>> {
    const url: string =
      this.getTicketUrl(companyId, plantId, ticketId) + '/close';
    return this.standardPut<void>(url, []);
  }

  openTicket(
    companyId: number,
    plantId: number,
    ticketId: number,
  ): Promise<AxiosResponse<void>> {
    const url: string =
      this.getTicketUrl(companyId, plantId, ticketId) + '/open';
    return this.standardPut<void>(url, []);
  }

  destroyTicket(
    companyId: number,
    plantId: number,
    ticketId: number,
  ): Promise<AxiosResponse<void>> {
    const url: string =
      this.getTicketUrl(companyId, plantId, ticketId) + '/close';
    return this.standardDelete<void>(url);
  }

  assignUserToTicket(
    companyId: number,
    plantId: number,
    ticketId: number,
    userId: number,
  ): Promise<AxiosResponse<void>> {
    const url: string =
      this.getTicketUrl(companyId, plantId, ticketId) + '/assign';
    return this.standardPost<void>(url, { user_id: userId });
  }

  unAssignUserToTicket(
    companyId: number,
    plantId: number,
    ticketId: number,
    userId: number,
  ): Promise<AxiosResponse<void>> {
    const url: string =
      this.getTicketUrl(companyId, plantId, ticketId) + '/unassign';
    return this.standardPost<void>(url, { user_id: userId });
  }

  getTicketOwningCompany(
    ticketId: number,
  ): Promise<AxiosResponse<CompanyJson>> {
    const url: string = StringUtils.stringFormat(URL_TICKET_OWNING_COMPANY, [
      ticketId,
    ]);
    return this.standardGet(url, {});
  }

  getTicketUrl(companyId: number, plantId: number, ticketId: number): string {
    return StringUtils.stringFormat(URL_TICKET_VIA_COMPANY_AND_PLANT, [
      companyId,
      plantId,
      ticketId,
    ]);
  }
}
